import { useEffect, useMemo, useRef, useState } from "react";
import { userSearchOptionsSelector } from "../../recoil/selectors/userSearchOptionsSelector";
import { useRecoilValue } from "recoil";
import { loginUserSelector } from "../../recoil/selectors/loginUserSelector";
import { isGeneralUser, isNotGeneralUser } from "../../constants/roles";
import { baseSearchOptionsSelector } from "../../recoil/selectors/baseSearchOptionsSelector";
import {
  RequestBulkRegisterAmazonItemsRequest,
  RequestCheckSellerIdRequest,
  RequestGetConnectLwasStautsRequest,
  RequestGetListingsItemsRequest,
  RequestRegistAmazonItemsRequest,
  RequestResetSyncItemsRequest,
  RequestSetItem,
  ResponseListingsItemResponse,
} from "../../services/OpenAPI";
import { useApiRegisterAmazonItems } from "../api/useApiRegisterAmazonItems";
import {
  CsvRow,
  GroupedCsvRow,
  parseCsv,
  ParseCsvGroupedResult,
} from "../../utils/parseCsv";
import { useApiBulkRegisterAmazonItems } from "../api/useApiBulkRegisterAmazonItems";
import { Note } from "../../components/NoteEditor";
import { useApiGetListingsItems } from "../api/useApiGetListingsItems";
import { useApiCheckSellerId } from "../api/useApiCheckSellerId";
import Papa from "papaparse";
import { useApiResetSyncItems } from "../api/useApiResetSyncItems";
import { useApiGetConnectLwaStatus } from "../api/useApiGetConnectLwaStatus";
import { ListingsSyncSeverity, useListingsSync } from "./useListingsSync";

interface MismatchRow {
  type: "正" | "誤" | "重複";
  sku: string;
  fnsku: string;
  asin: string;
  reason: string;
}

export type SetRowValues = {
  jan: string;
  name: string;
  setCount: string;
  deliveryCount: string; // 納品数
  expectedCount: string; // 予定入荷総数
  shippedCount?: string; // 実出荷単位数
  receivedCount?: string; // 実入荷総数
  expiryDate: Date | null;
  storageLocation?: string;
};

export const useProductRegistration = () => {
  const { registerAmazonItems, isLoading: isRegisterLoading } =
    useApiRegisterAmazonItems();
  const {
    bulkRegisterAmazonItems,
    error: bulkRegisterError,
    setError: setBulkRegisterError,
  } = useApiBulkRegisterAmazonItems();
  const {
    getListingsItems,
    listingsItems,
    setListingsItems,
    error: listingsItemsError,
    isLoading: isListingsItemsLoading,
  } = useApiGetListingsItems();
  const { checkSellerId } = useApiCheckSellerId();
  const { resetSyncItems, isLoading: isResetSyncItemsLoading } =
    useApiResetSyncItems();
  const { getConnectLwaStatus } = useApiGetConnectLwaStatus();

  const userSearchOptions = useRecoilValue(userSearchOptionsSelector);
  const baseSearchOptions = useRecoilValue(baseSearchOptionsSelector);
  const loginUser = useRecoilValue(loginUserSelector);

  // 「検索」で同期が始まった場合、完了後に自動で検索し直す
  const searchAfterSyncRef = useRef(false);
  const {
    isSyncing: syncItemsStatus,
    isStartingSync: isSyncItemsLoading,
    startSync,
    awaitSync,
    cancelAwaitSync,
  } = useListingsSync({
    tenantId: loginUser?.tenantId,
    userId: loginUser?.id,
    onMessage: (message, severity) => showToast(message, severity),
    onCompleted: (status) => {
      const shouldSearch = searchAfterSyncRef.current;
      searchAfterSyncRef.current = false;
      // 取り込めた商品が無い場合は再検索しても同期が繰り返されるだけなので行わない
      if (shouldSearch && status.result === "success" && (status.importedCount ?? 0) > 0) {
        handleFbaSearch();
      }
    },
  });

  // FBA検索
  const [keyword, setKeyword] = useState("");

  // 基本情報
  const [amazonItemName, setAmazonItemName] = useState("");
  const [setCount, setSetCount] = useState("");
  const [deliveryCount, setDeliveryCount] = useState<string>("");
  const [deliveryUnit, setDeliveryUnit] = useState<string>("個");
  const [expectedCount, setExpectedCount] = useState("");
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [storageLocation, setStorageLocation] = useState("");
  const [notes, setNotes] = useState<Note[]>([]);

  // 識別情報
  const [asin, setAsin] = useState("");
  const [sku, setSku] = useState("");
  const [fnsku, setFnsku] = useState("");
  const [jan, setJan] = useState("");
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedBaseId, setSelectedBaseId] = useState<number | null>(null);

  const [productSetValues, setProductSetValues] = useState<
    Record<number, SetRowValues>
  >({});

  const [rows, setRows] = useState<number[]>([]);
  const [rowDates, setRowDates] = useState<Record<number, Date | null>>({});

  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState<ListingsSyncSeverity>(
    "success",
  );
  const showToast = (message: string, severity: ListingsSyncSeverity) => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [fbaDialogOpen, setFbaDialogOpen] = useState(false);
  const [importSuccessCount, setImportSuccessCount] = useState(0);
  const [importFailCount, setImportFailCount] = useState(0);
  const [parsedData, setParsedData] = useState<CsvRow[]>([]);
  const [groupData, setGroupData] = useState<GroupedCsvRow[]>([]);
  const [syncDialog, setSyncDialog] = useState(false);
  const [resetSyncDialog, setResetSyncDialog] = useState(false);
  const [isNeedConfimationItem, setIsNeedConfimationItem] = useState(false);
  const [needConfimationWarningDialog, setNeedConfimationWarningDialog] =
    useState(false);
  const [isApiLink, setIsApiLink] = useState(false);
  // 連携状態を一度でも取得できたか（初回描画時の警告ちらつきを防ぐ）
  const [isLwaStatusChecked, setIsLwaStatusChecked] = useState(false);

  const handleCloseToast = () => {
    setBulkRegisterError(null);
    setToastOpen(false);
  };

  const userOptions = useMemo(() => {
    return (
      userSearchOptions?.map((user) => ({
        label: `${user.userNumber ?? ""}_${user.lastName ?? ""}${
          user.firstName ?? ""
        }`,
        id: user.id!,
      })) ?? []
    );
  }, [userSearchOptions]);

  const baseOptions = useMemo(() => {
    return (
      baseSearchOptions?.map((base) => ({
        label: `${base.name}`,
        id: base.id!,
      })) ?? []
    );
  }, [baseSearchOptions]);

  const handleUserChange = (userId: number | null) => {
    setSelectedUserId(userId);
    // ここでフィルタ処理やAPI呼び出し
  };

  const handleBaseChange = (baseId: number | null) => {
    setSelectedBaseId(baseId);
    // ここでフィルタ処理やAPI呼び出し
  };

  const handleAddRow = () => {
    if (rows.length === 0) {
      const newId1 = 1;
      const newId2 = 2;
      setRows([newId1, newId2]);
      setRowDates({
        [newId1]: null,
        [newId2]: null,
      });
      setProductSetValues((prev) => ({
        ...prev,
        [newId1]: {
          jan: jan,
          name: "",
          setCount: "",
          deliveryCount: deliveryCount,
          expectedCount: "",
          shippedCount: "",
          receivedCount: "",
          expiryDate: null,
          storageLocation: "",
        },
      }));
    } else {
      const nextId = Math.max(...rows) + 1;
      setRows((prev) => [...prev, nextId]);
      setRowDates((prev) => ({ ...prev, [nextId]: null }));
    }
  };

  const handleRowDateChange = (id: number, date: Date | null) => {
    setRowDates((prev) => ({ ...prev, [id]: date }));
  };

  const handleDeleteRow = (id: number) => {
    setRows((prev) => {
      if (prev.length <= 2) {
        const _id = id === 1 ? 2 : 1;
        setJan(productSetValues[_id].jan);
        setRowDates({});
        return [];
      } else {
        const updated = prev.filter((rowId) => rowId !== id);
        const newDates = { ...rowDates };
        delete newDates[id];
        setRowDates(newDates);
        return updated;
      }
    });
  };

  const handleProductSetValueChange = (id: number, newValues: SetRowValues) => {
    // 既存の値を取得
    const prev = productSetValues[id] ?? {
      setCount: "0",
      deliveryCount: "0",
      expectedCount: "0",
      jan: "",
      name: "",
      expiryDate: null,
    };

    // 現在のセット数と納品数（文字列→数値）
    const setCount = newValues.setCount ?? prev.setCount;
    const deliveryCount = newValues.deliveryCount ?? prev.deliveryCount;

    let expectedCount = newValues.expectedCount;

    // setCount または deliveryCount のどちらかが変わった場合のみ再計算
    const isSetCountChanged = newValues.setCount !== undefined;
    const isDeliveryCountChanged = newValues.deliveryCount !== undefined;

    if (isSetCountChanged || isDeliveryCountChanged) {
      const set = Number(setCount);
      const delivery = Number(deliveryCount);
      if (!isNaN(set) && !isNaN(delivery)) {
        expectedCount = String(set * delivery);
      }
    }

    setProductSetValues((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        ...newValues,
        expectedCount,
      },
    }));
  };

  const resetForm = () => {
    // 基本情報
    setAmazonItemName("");
    setSetCount("");
    setDeliveryCount("");
    setExpectedCount("");
    setSelectedDate(null);
    setStorageLocation("");

    // 識別情報
    setAsin("");
    setSku("");
    setFnsku("");
    setJan("");
    if (isNotGeneralUser(loginUser?.permissionId)) {
      setSelectedUserId(null);
      setSelectedBaseId(null);
    }

    // セット情報
    setRows([]);
    setRowDates({});
    setProductSetValues({});
  };

  const registerItemsAsync = async (req: RequestRegistAmazonItemsRequest) => {
    const res = await registerAmazonItems(req);
    if (res) {
      resetForm();
      setNotes([]);
      setToastMessage("登録が完了しました");
      setToastSeverity("success");
      setToastOpen(true);
    } else {
      setToastMessage("登録に失敗しました");
      setToastSeverity("error");
      setToastOpen(true);
    }
  };

  // 商品の登録先ユーザー。オーナー・拠点管理者は選択したユーザー、
  // 一般ユーザーは選択欄が無いため自分自身になる。
  const registerUserId = selectedUserId ?? loginUser?.id;

  const loginUserName = `${loginUser?.lastName ?? ""} ${
    loginUser?.firstName ?? ""
  }`;

  // 備考の記名と注記を決める。
  // ・セラー要確認商品：確認を依頼した本人（ログインユーザー）を記名する
  // ・それ以外：登録先ユーザーを記名し、管理者が他ユーザー名義で登録した場合は注記を添える
  const resolveMemoSignature = (isNeedConfirm: boolean) => {
    if (isNeedConfirm) return { userName: loginUserName, suffix: "" };

    const registerUser = userSearchOptions?.find(
      (user) => user.id === registerUserId
    );
    return {
      userName: registerUser
        ? `${registerUser.lastName ?? ""} ${registerUser.firstName ?? ""}`
        : loginUserName,
      suffix:
        registerUserId !== loginUser?.id ? "（管理者代理登録時に入力）" : "",
    };
  };

  // 備考欄の入力を「日時 ユーザー名：本文」形式に整形する。
  // この形式でないと商品情報ダイアログ側でパースできず、備考が表示されない。
  const formatNotes = (isNeedConfirm: boolean) => {
    const { userName, suffix } = resolveMemoSignature(isNeedConfirm);
    return notes
      .map((note) => {
        let base = `${note.timestamp} ${userName}：${note.content}${suffix}`;
        if (note.imageUrl) {
          base += ` | imageUrl=${note.imageUrl}`;
        }
        return base;
      })
      .join("\n");
  };

  const handleRegistItems = () => {
    if (checkValidate()) return;

    if (selectedBaseId === null) return;
    if (!loginUser || !loginUser.tenantId || !loginUser.id) return;
    // セットがある場合
    if (rows.length > 0) {
      const setItems: RequestSetItem[] = rows.map((id) => {
        const values = productSetValues[id];
        const item: RequestSetItem = {
          jan: values?.jan ?? "",
          name: values?.name ?? "",
          setCount: Number(values?.setCount ?? 0),
          arriveCount: Number(values?.deliveryCount ?? 0),
          expectedCount: Number(values?.expectedCount ?? 0),
        };

        if (values?.expiryDate) {
          item.expiryDate = values.expiryDate.toISOString();
        }

        return item;
      });
      const formattedMemo = formatNotes(isNeedConfimationItem);

      const req: RequestRegistAmazonItemsRequest = {
        tenantId: loginUser?.tenantId,
        amazonItemName: amazonItemName,
        setCount: Number(setCount) ?? 0,
        arriveCount: Number(deliveryCount),
        deliveryUnit: deliveryUnit,
        sumNum: 0, //Number(expectedCount) ?? 0,
        expectedCount: Number(expectedCount) ?? 0,
        asin: asin,
        fnsku: fnsku,
        sku: sku,
        baseId: selectedBaseId,
        userId: selectedUserId ?? loginUser?.id,
        setItems: setItems,
        memo: formattedMemo,
        isNeedConfimationItem: isNeedConfimationItem,
        cellerUserId: isNeedConfimationItem
          ? Number(selectedUserId)
          : undefined,
        cellerBaseId: selectedBaseId,
      };
      registerItemsAsync(req);
    }
    // セットがない場合
    else {
      const formattedMemo = formatNotes(isNeedConfimationItem);

      const req: RequestRegistAmazonItemsRequest = {
        tenantId: loginUser?.tenantId,
        amazonItemName: amazonItemName,
        setCount: Number(setCount) ?? 0,
        arriveCount: Number(deliveryCount) ?? 0,
        deliveryUnit: deliveryUnit,
        sumNum: 0,
        expectedCount: Number(expectedCount) ?? 0,
        storageLocation: storageLocation,
        asin: asin,
        fnsku: fnsku,
        jan: jan,
        sku: sku,
        memo: formattedMemo,
        baseId: selectedBaseId,
        userId: selectedUserId ?? loginUser?.id,
        isNeedConfimationItem: isNeedConfimationItem,
        cellerUserId: isNeedConfimationItem
          ? Number(selectedUserId)
          : undefined,
        cellerBaseId: selectedBaseId,
      };
      if (selectedDate) {
        req.expiryDate = selectedDate.toISOString();
      }
      registerItemsAsync(req);
    }
  };

  const handleFbaSearch = async () => {
    if (!loginUser?.id) return;
    const reqSellerId: RequestCheckSellerIdRequest = {
      userId: loginUser.id,
    };
    const IsSellerId = await checkSellerId(reqSellerId);
    if (!IsSellerId) {
      setToastMessage(
        "セラーIDが設定されていません。ユーザー情報ページから設定してください。",
      );
      setToastSeverity("error");
      setToastOpen(true);
      return;
    }
    const req: RequestGetListingsItemsRequest = {
      userId: loginUser.id,
      tenantId: loginUser.tenantId,
    };
    const result = await getListingsItems(req);
    if (result?.syncStarted) {
      // 商品情報が未取得のため同期が始まった。完了後に自動で検索結果を表示する
      searchAfterSyncRef.current = true;
      showToast(
        "商品情報を取得するため、セラーセントラルとの同期を開始しました。<br />完了すると自動で検索結果を表示します。",
        "info",
      );
      awaitSync();
    }
  };

  useEffect(() => {
    if (!listingsItems || listingsItems === null || dialogOpen) return;
    setFbaDialogOpen(true);
  }, [listingsItems]);

  const handleSelectListingsItem = (item: ResponseListingsItemResponse) => {
    for (let i = rows.length; i > 0; i--) {
      handleDeleteRow(rows.length);
    }
    setFbaDialogOpen(false);
    setAmazonItemName(item.itemName ?? "");
    setSetCount(String(item.packageQuantity) ?? "1");
    // setDeliveryCount(String(item.packageQuantity) ?? "0");
    setAsin(item.asin ?? "");
    setSku(item.sku ?? "");
    setFnsku(item.fnsku ?? "");
    if (item.jan?.length === 0 || item.jan?.length === 1) {
      if (item.jan) {
        setJan(item.jan?.length === 0 ? "" : (item.jan[0] ?? ""));
      }
    } else {
      const janList = item.jan ?? [];

      // janの数だけ行追加
      for (let i = 0; i < janList.length; i++) {
        handleAddRow();
      }
      setTimeout(() => {
        setProductSetValues((prev) => {
          const updated = { ...prev };
          let index = 0;
          for (const id of Object.keys(updated).map(Number)) {
            updated[id] = {
              ...updated[id],
              jan: janList[index] ?? "",
              name: item.itemName ?? "",
              setCount: "1",
              // deliveryCount: String(item.packageQuantity ?? "1"),
              // expectedCount: String(item.packageQuantity ?? "1"),
              expiryDate: null,
              storageLocation: "",
            };
            index++;
          }
          return updated;
        });
      }, 100);
    }
  };

  useEffect(() => {
    if (listingsItemsError === null) return;
    if (listingsItemsError === "出品者IDが見つかりません") {
      console.log("出品者IDが見つかりません");
    } else if (listingsItemsError === "Amazon APIの取得に失敗しました") {
      console.log("Amazon APIの取得に失敗しました");
    } else if (listingsItemsError === "リクエストに不備があります") {
      console.log("リクエストに不備があります");
    } else {
      console.log("予期しないエラー");
    }
  }, [listingsItemsError]);

  useEffect(() => {
    if (rows.length > 0) {
      setExpectedCount("");
      setSelectedDate(null);
      setStorageLocation("");
      setJan("");
    }
  }, [rows]);

  useEffect(() => {
    // セットがあるときはすべての deliveryCount に反映
    if (rows.length > 0) {
      const updated: Record<number, SetRowValues> = {};
      rows.forEach((id) => {
        updated[id] = {
          ...productSetValues[id],
          deliveryCount: deliveryCount,
        };
      });
      setProductSetValues(updated);
    }
  }, [deliveryCount, rows]);

  useEffect(() => {
    if (rows.length > 1) {
      setSetCount(String(rows.length));
    } else {
      setSetCount("1");
    }
  }, [rows]);

  useEffect(() => {
    const _expectedCount = Number(deliveryCount) * Number(setCount);
    setExpectedCount(String(_expectedCount));
  }, [deliveryCount, setCount]);

  const compareAndExportMismatches = (
    groupedRows: GroupedCsvRow[],
    listingsItems: ResponseListingsItemResponse[],
    duplicateRows: CsvRow[],
  ) => {
    const mismatches: MismatchRow[] = [];

    // 誤データ
    for (const row of groupedRows) {
      const matched = listingsItems.find(
        (item) =>
          item.sku === row.sku &&
          item.fnsku === row.fnsku &&
          item.asin === row.asin,
      );

      if (!matched) {
        const skuMatch = listingsItems.some((item) => item.sku === row.sku);
        const fnskuMatch = listingsItems.some((item) => item.fnsku === row.fnsku);
        const asinMatch = listingsItems.some((item) => item.asin === row.asin);

        let reason: string;
        if (!skuMatch && !fnskuMatch && !asinMatch) {
          reason = "SKU・FNSKU・ASINいずれもAmazonに存在しない";
        } else {
          const mismatched: string[] = [];
          if (!skuMatch) mismatched.push("SKU");
          if (!fnskuMatch) mismatched.push("FNSKU");
          if (!asinMatch) mismatched.push("ASIN");
          reason = `${mismatched.join("・")}が不一致`;
        }

        mismatches.push({
          type: "誤",
          sku: row.sku,
          fnsku: row.fnsku,
          asin: row.asin,
          reason,
        });
      }
    }

    // 重複データ
    for (const dup of duplicateRows) {
      mismatches.push({
        type: "重複",
        sku: dup.sku,
        fnsku: dup.fnsku,
        asin: dup.asin,
        reason: "CSV内に重複あり",
      });
    }

    if (mismatches.length > 0) {
      const csv = Papa.unparse(mismatches);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.setAttribute("download", "mismatched_items.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      // console.log("不一致はありませんでした。");
    }
  };

  // セラー要確認商品は、未登録の商品が届いた等の不備対応で使う機能のため、
  // CSVでの一括取込とは用途が異なる。チェックが入ったままでは取込に進ませない。
  const handleOpenCsvImport = () => {
    if (isNeedConfimationItem) {
      setNeedConfimationWarningDialog(true);
      return;
    }
    setDialogOpen(true);
  };

  const handleFileDrop = async (file: File) => {
    try {
      const result: ParseCsvGroupedResult = await parseCsv(file);
      let matchedRows: typeof result.groupedRows = [];
      let missMatchedRows: typeof result.groupedRows = [];

      if (isApiLink) {
        // APIリンクが true の場合は listingsItems と比較
        matchedRows = result.groupedRows.filter((row) =>
          listingsItems!.items?.some(
            (item) =>
              item.sku === row.sku &&
              item.fnsku === row.fnsku &&
              item.asin === row.asin,
          ),
        );

        missMatchedRows = result.groupedRows.filter(
          (row) =>
            !listingsItems!.items?.some(
              (item) =>
                item.sku === row.sku &&
                item.fnsku === row.fnsku &&
                item.asin === row.asin,
            ),
        );
      } else {
        // APIリンクが false の場合は全件を matchedRows に
        matchedRows = result.groupedRows;
        missMatchedRows = [];
      }

      setGroupData(matchedRows);
      setParsedData(result.validRows);
      setImportSuccessCount(matchedRows.length);
      setImportFailCount(missMatchedRows.length + result.duplicateRows.length);
      if (isApiLink) {
        compareAndExportMismatches(
          result.groupedRows,
          listingsItems!.items || [],
          result.duplicateRows,
        );
      } else if (result.duplicateRows.length > 0) {
        compareAndExportMismatches([], [], result.duplicateRows);
      }
      setConfirmOpen(true);
    } catch (err) {
      console.error("CSVの解析に失敗しました:", err);
      // エラートーストやダイアログなど表示すると親切
    }
  };

  const bulkRegisterItemsAsync = async (
    req: RequestBulkRegisterAmazonItemsRequest,
  ) => {
    const res = await bulkRegisterAmazonItems(req);
    if (res?.items && res?.items.length > 0) {
      // resetForm();
      setToastMessage(`${res?.items.length}件登録が完了しました`);
      setToastSeverity("success");
      setToastOpen(true);
    } else {
      setToastMessage("登録に失敗しました");
      setToastSeverity("error");
      setToastOpen(true);
    }
  };

  useEffect(() => {
    if (bulkRegisterError === null) return;
    setToastMessage("登録に失敗しました");
    setToastSeverity("error");
    setToastOpen(true);
  }, [[bulkRegisterError]]);

  // CSVの備考を、備考欄の入力と同じ「日時 ユーザー名：本文」形式に整形する。
  // CSV取込は常にセラー要確認商品ではないため、記名は登録先ユーザーになる。
  const formatCsvMemo = (memo: string) => {
    if (memo.trim() === "") return "";
    const timestamp = new Date()
      .toLocaleString("ja-JP", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
      .replace(/\//g, "-");
    const { userName, suffix } = resolveMemoSignature(false);
    return `${timestamp} ${userName}：${memo}${suffix}`;
  };

  const handleOk = () => {
    if (selectedBaseId === null) return;
    if (parsedData.length === 0) return;
    if (!loginUser || !loginUser.tenantId || !loginUser.id) return;

    const items: RequestRegistAmazonItemsRequest[] = groupData.map((group) => ({
      asin: group.asin,
      fnsku: group.fnsku,
      jan: group.parentJan, // 親JANが"0"なら省略可（API仕様に応じて）
      amazonItemName: group.name,
      setCount: Number(group.setCount),
      arriveCount: Number(group.unitCount),
      deliveryUnit: "", // 必要に応じて設定
      memo: formatCsvMemo(group.memo),
      sku: group.sku,
      baseId: selectedBaseId,
      tenantId: loginUser?.tenantId!,
      userId: registerUserId!,
      expectedCount: 0,
      storageLocation: "",
      isNeedConfimationItem: false,
      setItems:
        group.children.length > 0
          ? group.children.map((child) => ({
              jan: child.jan,
              name: child.name,
              setCount: Number(child.setCount),
              arriveCount: Number(child.unitCount),
              expectedCount: 0,
            }))
          : undefined,
    }));

    const request: RequestBulkRegisterAmazonItemsRequest = { items };
    bulkRegisterItemsAsync(request);
  };

  const checkValidate = () => {
    const isEmpty = (v: string | undefined | null) => !v || v.trim() === "";
    let msg = "";
    let selectMsg = "";
    let errMsg = "";

    if (!isNeedConfimationItem) {
      if (isEmpty(amazonItemName)) msg += "・商品名\n";
      if (isEmpty(deliveryCount)) msg += "・納品数\n";
      if (isEmpty(deliveryUnit)) msg += "・納品単位\n";
      if (isEmpty(asin)) msg += "・ASIN\n";
      if (isEmpty(sku)) msg += "・SKU\n";
      if (isEmpty(fnsku)) msg += "・FNSKU\n";
      if (selectedBaseId === null || selectedBaseId === 0)
        selectMsg += "・拠点\n";
      if (selectedUserId === null) selectMsg += "・ユーザー\n";

      if (Number(setCount) === 0 || setCount === "") {
        errMsg += "<br />セット数に1以上を入力してください";
      }

      const commonInvalid =
        isEmpty(amazonItemName) ||
        isEmpty(deliveryUnit) ||
        isEmpty(asin) ||
        isEmpty(sku) ||
        isEmpty(fnsku) ||
        selectedBaseId === null ||
        selectedUserId === null;

      if (rows.length === 0) {
        // セットがない（単品登録）
        if (isEmpty(jan)) msg += "・JAN\n";
        if (isEmpty(setCount)) msg += "・セット数\n";

        if (
          commonInvalid ||
          isEmpty(jan) ||
          isEmpty(setCount) ||
          isEmpty(deliveryCount)
        ) {
          // トースト表示用
          if (msg.length > 0)
            msg =
              "以下の項目が未入力です。<br />" +
              msg.replace(/\n/g, "<br />") +
              "<br />";
          if (selectMsg.length > 0)
            selectMsg =
              "以下の項目が未選択です。<br />" +
              selectMsg.replace(/\n/g, "<br />");
          setToastMessage(msg + selectMsg);
          setToastSeverity("error");
          setToastOpen(true);
          return true;
        }

        return false;
      }

      let janMissing = false;
      let nameMissing = false;
      let setCountMissing = false;
      // let deliveryCountMissing = false;
      let expectedCountMissing = false;
      let totalChileSetCount = 0;
      for (const id of rows) {
        const v = productSetValues[id];
        if (!v) continue;

        if (isEmpty(v.jan)) janMissing = true;
        if (isEmpty(v.name)) nameMissing = true;
        if (v.setCount === "") setCountMissing = true;
        // if (v.deliveryCount === "") deliveryCountMissing = true;
        if (v.expectedCount === "") expectedCountMissing = true;
        totalChileSetCount += Number(v.setCount);
      }

      if (janMissing) msg += "・JAN（子）\n";
      if (nameMissing) msg += "・商品名（子）\n";
      if (setCountMissing) msg += "・セット数（子）\n";
      // if (deliveryCountMissing) msg += "・納品数（子）\n";
      if (expectedCountMissing) msg += "・予定入荷総数（子）\n";

      if (Number(setCount) !== totalChileSetCount) {
        errMsg += "<br />親のセット数と子のセット数が異なります。";
      }

      if (
        commonInvalid ||
        janMissing ||
        nameMissing ||
        setCountMissing ||
        // deliveryCountMissing ||
        expectedCountMissing ||
        Number(setCount) !== totalChileSetCount
      ) {
        // トースト表示用
        if (msg.length > 0)
          msg =
            "以下の項目が未入力です。<br />" +
            msg.replace(/\n/g, "<br />") +
            "<br />";
        if (selectMsg.length > 0)
          selectMsg =
            "以下の項目が未選択です。<br />" +
            selectMsg.replace(/\n/g, "<br />");
        setToastMessage(msg + selectMsg + errMsg);
        setToastSeverity("error");
        setToastOpen(true);
        return true;
      }
    } else {
      if (selectedBaseId === null) selectMsg += "・拠点\n";
      if (selectedUserId === null) selectMsg += "・ユーザー\n";
      if (selectMsg.length > 0)
        selectMsg =
          "以下の項目が未選択です。<br />" + selectMsg.replace(/\n/g, "<br />");

      if (notes.length === 0) {
        msg =
          "セラー要確認商品の場合は、備考にメッセージを追加してください。<br />";
      }
      if (!isEmpty(msg) || !isEmpty(selectMsg)) {
        setToastMessage(msg + selectMsg);
        setToastSeverity("error");
        setToastOpen(true);
        return true;
      }
    }

    return false;
  };

  const syncItems = async () => {
    searchAfterSyncRef.current = false;
    await startSync();
  };

  // 同期が中断されて「同期中」のまま解除されない場合の復旧用
  const handleResetSyncItems = async () => {
    const req: RequestResetSyncItemsRequest = {
      tenantId: loginUser?.tenantId,
      userId: loginUser?.id,
    };
    const result = await resetSyncItems(req);
    if (result) {
      searchAfterSyncRef.current = false;
      cancelAwaitSync();
      setToastMessage("同期中状態を解除しました");
      setToastSeverity("success");
    } else {
      setToastMessage("同期中状態の解除に失敗しました");
      setToastSeverity("error");
    }
    setToastOpen(true);
  };

  const fetchConnectLwaStatus = async () => {
    const req: RequestGetConnectLwasStautsRequest = {
      tenantId: loginUser?.tenantId,
      userId: loginUser?.id,
    };
    const response = await getConnectLwaStatus(req);
    setIsApiLink(response ?? false);
    setIsLwaStatusChecked(true);
  };

  // 一般ユーザーの場合、拠点とユーザーを自動設定
  useEffect(() => {
    if (isGeneralUser(loginUser?.permissionId)) {
      setSelectedBaseId(loginUser?.baseId!);
      setSelectedUserId(loginUser?.id!);
    }
  }, [loginUser?.permissionId, loginUser?.baseId, loginUser?.id]);

  // マウント時に一度だけ実行（同期状態のポーリングは useListingsSync が行う）
  useEffect(() => {
    fetchConnectLwaStatus();
  }, []);

  useEffect(() => {
    if (!dialogOpen) {
      setListingsItems(null);
      return;
    }
    if (isApiLink) {
      handleFbaSearch();
    }
  }, [dialogOpen]);

  useEffect(() => {
    if (!fbaDialogOpen) {
      setListingsItems(null);
      return;
    }
  }, [fbaDialogOpen]);

  return {
    loginUser,
    userOptions,
    baseOptions,
    handleUserChange,
    handleBaseChange,
    selectedDate,
    setSelectedDate,
    rows,
    rowDates,
    handleAddRow,
    handleRowDateChange,
    handleDeleteRow,
    amazonItemName,
    setAmazonItemName,
    setCount,
    setSetCount,
    deliveryCount,
    setDeliveryCount,
    deliveryUnit,
    setDeliveryUnit,
    expectedCount,
    setExpectedCount,
    storageLocation,
    setStorageLocation,
    notes,
    setNotes,
    asin,
    setAsin,
    sku,
    setSku,
    fnsku,
    setFnsku,
    jan,
    setJan,
    selectedUserId,
    setSelectedUserId,
    selectedBaseId,
    setSelectedBaseId,
    productSetValues,
    handleProductSetValueChange,
    handleRegistItems,
    isRegisterLoading,
    toastOpen,
    toastMessage,
    toastSeverity,
    handleCloseToast,
    dialogOpen,
    setDialogOpen,
    confirmOpen,
    setConfirmOpen,
    importSuccessCount,
    setImportSuccessCount,
    importFailCount,
    setImportFailCount,
    setParsedData,
    handleFileDrop,
    handleOk,
    checkValidate,
    keyword,
    setKeyword,
    handleFbaSearch,
    isListingsItemsLoading,
    fbaDialogOpen,
    setFbaDialogOpen,
    listingsItems,
    setListingsItems,
    handleSelectListingsItem,
    syncItems,
    isSyncItemsLoading,
    syncDialog,
    setSyncDialog,
    syncItemsStatus,
    resetSyncDialog,
    setResetSyncDialog,
    handleResetSyncItems,
    isResetSyncItemsLoading,
    isNeedConfimationItem,
    setIsNeedConfimationItem,
    needConfimationWarningDialog,
    setNeedConfimationWarningDialog,
    handleOpenCsvImport,
    isApiLink,
    isLwaStatusChecked,
    memoUserName: resolveMemoSignature(isNeedConfimationItem).userName,
  };
};
